import React, { useEffect } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import styled from 'styled-components';

import Headline1 from '@twicapp/ui/components/Headline1';
import Headline2 from '@twicapp/ui/components/Headline2';
import Divider from '@twicapp/ui/components/Divider';
import Subtitle1 from '@twicapp/ui/components/Subtitle1';
import Title1 from '@twicapp/ui/components/Title1';
import Body1 from '@twicapp/ui/components/Body1';

import ProductListContainer from 'components/common/ProductListContainer';
import ProductCard from 'components/common/ProductCard';
import ProductListLoading from 'components/common/ProductListLoading';

import useInfiniteQuerySearchProducts from 'hooks/useInfiniteQuerySearchProducts';

import type { Product } from 'utils/algolia';
import * as Amplitude from 'utils/amplitude';

import { SCREEN_WIDTHS } from '@twicapp/ui/constants/breakpoints';

interface Company {
  features: {
    stipend_config: {
      display_as_amount: boolean;
    };
  };
}

const WelcomeHeader = styled.div`
  margin-bottom: 23px;
`;

const WelcomeText = styled(Headline1)``;

const RelatedResultsHeader = styled.div`
  margin-bottom: 16px;
`;

const RelatedResultsText = styled(Headline2)``;

const DividerContainer = styled.div`
  margin-bottom: 32px;
`;

const ResultsGridContainer = styled.div`
  margin-top: 16px;
  margin-bottom: 68px;
`;

const ResultsContainer = styled.div``;

const ResultsHeader = styled(Subtitle1)`
  color: ${({
    theme: {
      colors: {
        text: { secondary },
      },
    },
  }) => secondary};
`;

const ResultContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  column-gap: 24px;
  grid-row-gap: 56px;

  @media (width: ${SCREEN_WIDTHS.TABLET_PORTRAIT}) {
    display: flex;
    flex-direction: column;
    gap: 32px;
  }
`;

const ResultsEmptyContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${({
    theme: {
      colors: {
        background: { secondary },
      },
    },
  }) => secondary};
  border-radius: 7px;
  padding: 48px 0;
  margin: 16px 0px 56px;
  margin-top: 16px;
`;

const ResultsEmptyHeader = styled(Title1)``;
const ResultsEmptyMessage = styled(Body1)``;

const ProductsContainer = styled.div``;

const onClickProductCard = (event: string, product: Product, index: number) => {
  Amplitude.logEvent(event, {
    card_position: index,
    product_id: product.product_id,
    vendor_id: product.vendor_id,
  });
};

const renderProductCard = ({
  algolia_product,
  company,
  employee_country_exchange_rate,
  onClick,
}: {
  algolia_product: Product;
  company: Company;
  employee_country_exchange_rate: number;
  onClick: () => ReturnType<typeof onClickProductCard>;
}) => {
  const {
    id,
    product_id,
    vendor_id,
    coupon_code_available,
    coupon_code_locations,
    product_instruction_image,
    product_image_url: image_url,
    product_title: title,
    vendor_title,
    term,
    usd_special_price,
    usd_msrp,
  } = algolia_product;
  return (
    <ProductCard
      key={id}
      id={product_id}
      employee_country_exchange_rate={employee_country_exchange_rate}
      vendor={{
        id: vendor_id,
        coupon_code_available,
        coupon_code_locations,
        title: vendor_title,
      }}
      company={company}
      product={{
        msrp: usd_msrp,
        special_price: usd_special_price,
        term,
        id: product_id,
      }}
      image={product_instruction_image[0] || image_url}
      title={title}
      onClick={onClick}
    />
  );
};

const RelatedProducts = ({
  company,
  employee_country_exchange_rate,
  related_products,
}: {
  company: Company;
  employee_country_exchange_rate: number;
  related_products: Product[];
}) => {
  useEffect(() => {
    Amplitude.logEvent(Amplitude.EVENTS.RelatedSearchResultsViewed, {
      related_products_returned: related_products.map(({ id }) => id),
    });
  }, [related_products]);

  return (
    <>
      <RelatedResultsHeader>
        <RelatedResultsText>You Might Also Like...</RelatedResultsText>
      </RelatedResultsHeader>
      <DividerContainer>
        <Divider />
      </DividerContainer>
      <ResultsGridContainer>
        <ResultContainer>
          {related_products!.map((algolia_product, index) =>
            renderProductCard({
              algolia_product,
              company,
              employee_country_exchange_rate,
              onClick: () =>
                onClickProductCard(
                  Amplitude.EVENTS.RelatedSearchResultsClicked,
                  algolia_product,
                  index
                ),
            })
          )}
        </ResultContainer>
      </ResultsGridContainer>
    </>
  );
};

const Products = ({
  products,
  total_products,
  company,
  employee_country_exchange_rate,
  has_next_page,
  fetchNextPage,
  related_products,
}: {
  products: Product[];
  total_products: number;
  company: Company;
  employee_country_exchange_rate: number;
  has_next_page: boolean;
  fetchNextPage: ReturnType<
    typeof useInfiniteQuerySearchProducts
  >['fetchNextPage'];
  related_products?: Product[];
}) => {
  useEffect(() => {
    Amplitude.logEvent(Amplitude.EVENTS.SearchResultsViewed, {
      total_results_returned: total_products,
      products_returned: products.map(({ id }) => id),
    });
  }, [products, total_products]);

  return (
    <ResultsContainer>
      <ResultsHeader>{`Showing ${total_products} results`}</ResultsHeader>
      <ResultsGridContainer>
        <InfiniteScroll
          loader={null}
          dataLength={products.length}
          next={() => fetchNextPage()}
          hasMore={has_next_page}
        >
          <ResultContainer>
            {products!.map((algolia_product, index) =>
              renderProductCard({
                algolia_product,
                company,
                employee_country_exchange_rate,
                onClick: () =>
                  onClickProductCard(
                    Amplitude.EVENTS.SearchResultsClicked,
                    algolia_product,
                    index
                  ),
              })
            )}
          </ResultContainer>
        </InfiniteScroll>
      </ResultsGridContainer>
      {related_products && related_products.length > 0 && (
        <RelatedProducts
          company={company}
          employee_country_exchange_rate={employee_country_exchange_rate}
          related_products={related_products}
        />
      )}
    </ResultsContainer>
  );
};

const DiscoverProducts = ({
  company,
  unpopular_products,
}: {
  company: Company;
  unpopular_products: Product[];
}) => {
  useEffect(() => {
    Amplitude.logEvent(Amplitude.EVENTS.DiscoverSearchResultsViewed, {
      unpopular_products_returned: unpopular_products.map(({ id }) => id),
    });
  }, [unpopular_products]);

  return (
    <>
      <RelatedResultsHeader>
        <RelatedResultsText>Discover More of Twic</RelatedResultsText>
      </RelatedResultsHeader>
      <DividerContainer>
        <Divider />
      </DividerContainer>
      <ResultsGridContainer>
        <ResultContainer>
          {unpopular_products!.map((algolia_product, index) =>
            renderProductCard({
              algolia_product,
              company,
              // These are already translated - need to improve consistency
              employee_country_exchange_rate: 1,
              onClick: () =>
                onClickProductCard(
                  Amplitude.EVENTS.DiscoverSearchResultsClicked,
                  algolia_product,
                  index
                ),
            })
          )}
        </ResultContainer>
      </ResultsGridContainer>
    </>
  );
};

const NoProducts = ({
  company,
  unpopular_products,
}: {
  company: Company;
  unpopular_products?: Product[];
}) => (
  <ResultsContainer>
    <ResultsHeader>No results found</ResultsHeader>
    <ResultsEmptyContainer>
      <ResultsEmptyHeader>
        We couldn't find what you're looking for at this time.
      </ResultsEmptyHeader>
      <ResultsEmptyMessage>
        Browse by account or category, or use another search term.
      </ResultsEmptyMessage>
    </ResultsEmptyContainer>
    {unpopular_products && unpopular_products.length > 0 && (
      <DiscoverProducts
        company={company}
        unpopular_products={unpopular_products}
      />
    )}
  </ResultsContainer>
);

const ProductSearchResults = ({
  company,
  products,
  employee_country_exchange_rate,
  total_products,
  query,
  has_next_page,
  fetchNextPage,
  related_products,
  unpopular_products,
  is_vendors_loading,
}: {
  company: Company;
  products?: Product[];
  employee_country_exchange_rate: number;
  total_products: number;
  query?: string;
  has_next_page: boolean;
  fetchNextPage: ReturnType<
    typeof useInfiniteQuerySearchProducts
  >['fetchNextPage'];
  related_products?: Product[];
  unpopular_products?: Product[];
  is_vendors_loading: boolean;
}) => (
  <ProductListContainer>
    <WelcomeHeader>
      <WelcomeText>
        {query ? `Search for "${query}"` : 'Showing all results'}
      </WelcomeText>
    </WelcomeHeader>
    <DividerContainer>
      <Divider />
    </DividerContainer>
    {is_vendors_loading ? (
      <ProductListLoading />
    ) : (
      <ProductsContainer>
        {!total_products ? (
          <NoProducts
            company={company}
            unpopular_products={unpopular_products}
          />
        ) : (
          <Products
            products={products!}
            total_products={total_products}
            company={company}
            employee_country_exchange_rate={employee_country_exchange_rate}
            has_next_page={has_next_page}
            fetchNextPage={fetchNextPage}
            related_products={related_products}
          />
        )}
      </ProductsContainer>
    )}
  </ProductListContainer>
);
export default ProductSearchResults;
